import React, { useEffect, useRef } from 'react';
import useTypewriter from '../hooks/useTypewriter';

const TYPED_STRINGS = ['Senior UI Developer.', 'Founder of Korsha.', 'Product Builder.'];

const Hero = ({ navTo }) => {
  const canvasRef = useRef(null);
  const typed = useTypewriter(TYPED_STRINGS);

  // Canvas particle system
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let rafId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize, { passive: true });

    const pts = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.3,
      dx: (Math.random() - 0.5) * 0.28,
      dy: (Math.random() - 0.5) * 0.28,
      a: Math.random() * 0.55 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts.forEach(p => {
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(138,107,196,${p.a})`;
        ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 90) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(138,107,196,${0.07 * (1 - d / 90)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      rafId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section id="home">
      <canvas id="heroCanvas" ref={canvasRef} />
      <div className="hero-glow hero-glow-1" />
      <div className="hero-glow hero-glow-2" />

      <div className="container">
        <div className="hero-grid">

          {/* LEFT: Text Content */}
          <div className="hero-left">
            <div className="hero-badge">
              <span className="badge-dot" />
              AVAILABLE FOR PROJECTS
            </div>
            <p className="hero-greeting">Hello, I'm</p>
            <h1 className="hero-name">
              SURESH<br />
              <span className="gradient-text">NARASIMMAN</span>
            </h1>
            <div className="hero-typed">
              <span>{typed}</span>
              <span className="cursor">|</span>
            </div>
            <p className="hero-tagline">
              Co-Founder &amp; Senior UI Developer at <span className="korsha-brand">KORSHA</span><br />
              Building end-to-end products — React frontends, Node.js backends,
              and everything in between.{' '}
              <em style={{ color: 'var(--text-primary)', fontStyle: 'normal' }}>The Future, Drawn to You.</em>
            </p>
            <div className="hero-actions">
              <button className="btn-primary" onClick={() => navTo('projects')}>View Projects ↗</button>
              <button className="btn-outline" onClick={() => navTo('contact')}>Let's Collaborate</button>
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="stat-val">9+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="hero-stat">
                <span className="stat-val">10+</span>
                <span className="stat-label">Projects Shipped</span>
              </div>
              <div className="hero-stat">
                <span className="stat-val">∞</span>
                <span className="stat-label">Stacks Covered</span>
              </div>
            </div>
          </div>

          {/* RIGHT: Korsha Orbital System */}
          <div className="hero-right">
            <div>
              <div className="orbital-system">
                <div className="orbital-core-glow" />
                <div className="orbit-ring-outer" />
                <div className="orbit-ring-inner" />
                <div className="orbit-dot-1" />
                <div className="orbit-dot-2" />
                <div className="orbital-center">
                  <svg width="52" height="52" viewBox="0 0 80 80" fill="none">
                    <circle cx="40" cy="40" r="36" stroke="#C9B0E9" strokeWidth="1.5" opacity=".4" />
                    <path d="M54 16 L24 40 L54 64" stroke="#C9B0E9" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="40" cy="12" r="3.5" fill="#C9B0E9" opacity=".9" />
                    <circle cx="68" cy="40" r="3.5" fill="#C9B0E9" opacity=".9" />
                    <circle cx="40" cy="68" r="3.5" fill="#C9B0E9" opacity=".9" />
                    <circle cx="12" cy="40" r="3.5" fill="#C9B0E9" opacity=".9" />
                  </svg>
                </div>
                <div className="skill-float sf-react">⚛ React</div>
                <div className="skill-float sf-node">🟢 Node.js</div>
                <div className="skill-float sf-ts">🔷 TypeScript</div>
                <div className="skill-float sf-mongo">🍃 MongoDB</div>
                <div className="skill-float sf-figma">🎨 Figma</div>
                <div className="skill-float sf-aws">🔥 Firebase</div>
              </div>
              <div className="orbital-tagline">THE FUTURE, DRAWN TO YOU</div>
            </div>
          </div>

        </div>
      </div>

      <div className="scroll-hint">
        <div className="scroll-wheel" />
        <span>Scroll to explore</span>
      </div>
    </section>
  );
};

export default Hero;
